import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, User, ChevronRight, ArrowLeft, ArrowRight, Share2, Twitter, Linkedin, Facebook, Link2, Check } from 'lucide-react';
import { BlogPageSEO } from '@/components/seo';
import Header from '@/components/public/Header';
import Footer from '@/components/public/Footer';
import BlogContent from '@/components/public/BlogContent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BASE_URL = 'https://starteaseai.com';

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

// Category colors
const getCategoryColor = (category) => {
  const colors = {
    formation: 'bg-emerald-100 text-emerald-700',
    compliance: 'bg-blue-100 text-blue-700',
    tax: 'bg-amber-100 text-amber-700',
    guides: 'bg-purple-100 text-purple-700',
    news: 'bg-rose-100 text-rose-700',
    'sales-growth': 'bg-emerald-100 text-emerald-700',
    'people-payroll': 'bg-blue-100 text-blue-700',
    'finance-operations': 'bg-amber-100 text-amber-700',
    'technology-ai': 'bg-indigo-100 text-indigo-700',
  };
  return colors[category?.toLowerCase()] || 'bg-slate-100 text-slate-700';
};

// Friendly category labels
const CATEGORY_LABELS = {
  'sales-growth': 'Sales & Growth',
  'people-payroll': 'People & Payroll',
  'finance-operations': 'Finance & Operations',
  'technology-ai': 'Technology & AI',
  formation: 'Business Formation',
  compliance: 'Compliance',
  tax: 'Tax & Finance',
  guides: 'Guides',
  news: 'News & Updates',
};
const categoryLabel = (c) => CATEGORY_LABELS[c?.toLowerCase()] || c;

// Share Button Component
const ShareButton = ({ icon: Icon, label, onClick, className = '' }) => (
  <button
    onClick={onClick}
    className={`p-2 rounded-full hover:bg-slate-100 transition-colors ${className}`}
    title={label}
    data-testid={`share-${label.toLowerCase()}`}
  >
    <Icon className="w-5 h-5" />
  </button>
);

// Related Post Card
const RelatedPostCard = ({ post }) => (
  <Link 
    to={`/blog/${post.slug}`}
    className="group block bg-white border border-slate-200 overflow-hidden hover:shadow-lg hover:border-emerald-500 transition-all duration-300"
    data-testid={`related-post-${post.slug}`}
  >
    <div className="aspect-video overflow-hidden bg-slate-100">
      {post.featured_image ? (
        <img 
          src={post.featured_image} 
          alt={post.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
          <span className="text-3xl text-slate-300">S</span>
        </div>
      )}
    </div>
    <div className="p-4">
      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getCategoryColor(post.category)}`}>
        {categoryLabel(post.category) || 'General'}
      </span>
      <h4 className="mt-2 font-semibold text-slate-900 group-hover:text-emerald-600 transition-colors line-clamp-2">
        {post.title}
      </h4>
      <p className="text-sm text-slate-500 mt-1">
        {formatDate(post.published_at)}
      </p>
    </div>
  </Link>
);

/**
 * BlogPostPage - Displays a single blog post with full SEO
 * Fetches data from GET /api/public/blog/{slug}
 */
const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/public/blog/${slug}`);
        setPost(response.data);
        setError(null);
        
        // Fetch related posts (same category or recent)
        if (response.data.category) {
          try {
            const relatedResponse = await axios.get(`${API}/public/blog/category/${response.data.category}`, {
              params: { limit: 4 }
            });
            // Filter out current post
            const filtered = (relatedResponse.data.posts || [])
              .filter(p => p.slug !== slug)
              .slice(0, 3);
            setRelatedPosts(filtered);
          } catch (relatedErr) {
            console.error('Error fetching related posts:', relatedErr);
          }
        }
      } catch (err) {
        console.error('Error fetching blog post:', err);
        setError(err.response?.data?.detail || 'Failed to load blog post');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchPost();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [slug]);

  // Share functions
  const shareUrl = `${BASE_URL}/blog/${slug}`;
  
  const shareOnTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post?.title || '')}`, '_blank');
  };
  
  const shareOnLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank');
  };
  
  const shareOnFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
  };
  
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center py-32" data-testid="blog-loading">
          <div className="animate-pulse text-lg text-slate-500">Loading article...</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex flex-col items-center justify-center py-32" data-testid="blog-error">
          <p className="text-red-500 mb-4">{error}</p>
          <Link to="/blog" className="text-emerald-600 font-medium hover:underline flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex flex-col items-center justify-center py-32" data-testid="blog-not-found">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Article not found</h2>
          <p className="text-slate-600 mb-6">The article you're looking for doesn't exist or has been removed.</p>
          <Link to="/blog" className="text-emerald-600 font-medium hover:underline flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="blog-post-page">
      {/* SEO Component */}
      <BlogPageSEO 
        post={post} 
        baseUrl={BASE_URL}
      />
      
      <Header />

      {/* Hero Section */}
      <section className="bg-[#1e293b] py-12 md:py-16" data-testid="post-hero">
        <div className="container-custom">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-slate-400 mb-6" data-testid="breadcrumb">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
            {post.category && (
              <>
                <ChevronRight className="w-4 h-4" />
                <Link 
                  to={`/blog/category/${post.category.toLowerCase()}`}
                  className="hover:text-white transition-colors"
                >
                  {categoryLabel(post.category)}
                </Link>
              </>
            )}
          </nav>

          <div className="max-w-3xl">
            {post.category && (
              <span className={`inline-block text-sm font-medium px-3 py-1 rounded-full mb-4 ${getCategoryColor(post.category)}`}>
                {categoryLabel(post.category)}
              </span>
            )}
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight" data-testid="blog-title">
              {post.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-slate-300">
              {post.author && (
                <span className="flex items-center gap-2" data-testid="blog-author">
                  <User className="w-4 h-4" />
                  {post.author}
                </span>
              )}
              {post.published_at && (
                <span className="flex items-center gap-2" data-testid="blog-date">
                  <Calendar className="w-4 h-4" />
                  {formatDate(post.published_at)}
                </span>
              )}
              {post.reading_time && (
                <span className="flex items-center gap-2" data-testid="blog-reading-time">
                  <Clock className="w-4 h-4" />
                  {post.reading_time} min read
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      {post.featured_image && (
        <div className="container-custom -mt-8 mb-12" data-testid="blog-featured-image">
          <div className="max-w-4xl mx-auto">
            <img 
              src={post.featured_image} 
              alt={post.title}
              className="w-full rounded-xl shadow-xl object-cover"
              style={{ maxHeight: '400px' }}
            />
          </div>
        </div>
      )}

      {/* Article Content */}
      <article className="py-12" data-testid="blog-article">
        <div className="container-custom">
          <div className="mx-auto" style={{ maxWidth: '720px' }}>
            {/* Excerpt */}
            {post.excerpt && (
              <div className="text-xl text-slate-600 mb-8 pb-8 border-b border-slate-200 italic" data-testid="blog-excerpt">
                {post.excerpt}
              </div>
            )}

            {/* Content */}
            <BlogContent content={post.content} testId="blog-content" />

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="mt-12 pt-8 border-t border-slate-200" data-testid="blog-tags">
                <h3 className="text-sm font-semibold text-slate-500 mb-3">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag, index) => (
                    <span 
                      key={index}
                      className="bg-slate-100 text-slate-700 text-sm px-3 py-1 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Share Section */}
            <div className="mt-8 pt-8 border-t border-slate-200" data-testid="share-section">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-500 flex items-center gap-2">
                  <Share2 className="w-4 h-4" />
                  Share this article
                </span>
                <div className="flex items-center gap-1">
                  <ShareButton icon={Twitter} label="Twitter" onClick={shareOnTwitter} className="text-slate-500 hover:text-[#1DA1F2]" />
                  <ShareButton icon={Linkedin} label="LinkedIn" onClick={shareOnLinkedIn} className="text-slate-500 hover:text-[#0A66C2]" />
                  <ShareButton icon={Facebook} label="Facebook" onClick={shareOnFacebook} className="text-slate-500 hover:text-[#1877F2]" />
                  <ShareButton 
                    icon={copied ? Check : Link2} 
                    label="Copy Link" 
                    onClick={copyLink} 
                    className={copied ? 'text-emerald-600' : 'text-slate-500'} 
                  />
                </div>
              </div>
            </div>

            {/* Author Bio */}
            {post.author && (
              <div className="mt-8 p-6 bg-slate-50 rounded-xl" data-testid="author-bio">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-xl font-semibold text-emerald-700">
                      {post.author.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">{post.author}</h4>
                    <p className="text-sm text-slate-600 mt-1">
                      StartEase Team member specializing in US business formation and compliance.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* CTA Section */}
            <div className="mt-12 p-8 bg-[#1e293b] rounded-xl text-center" data-testid="post-cta">
              <h3 className="text-2xl font-bold text-white mb-3">
                Ready to start your US business?
              </h3>
              <p className="text-slate-300 mb-6">
                Let StartEase handle your LLC or Corporation formation. Fast, compliant, and hassle-free.
              </p>
              <Link 
                to="/login"
                className="inline-flex items-center justify-center px-8 py-4 bg-emerald-500 text-white font-medium hover:bg-emerald-600 transition-colors rounded-lg"
              >
                Get Started Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-12 bg-slate-50" data-testid="related-posts-section">
          <div className="container-custom">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold text-slate-900">Related Articles</h2>
              <Link 
                to="/blog"
                className="text-emerald-600 font-medium hover:underline flex items-center gap-1"
              >
                View all <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <RelatedPostCard key={relatedPost.id || relatedPost.slug} post={relatedPost} />
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default BlogPostPage;
